import { get, pick } from 'lodash'
import { useTranslate } from 'ra-core'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  CREATE,
  ImageField,
  ImageInput,
  required,
  SelectInput,
  TextInput,
  UPDATE,
  useGetList,
  useGetOne,
  useRecordContext,
} from 'react-admin'
import { useFormContext, useWatch } from 'react-hook-form'

import AdvancedColorAutocompleteInput from '../../components/AdvancedColorAutocompleteInput'
import { AdvancedDateTimeInput } from '../../components/AdvancedDateTimeInputs'
import AdvancedNumberInput from '../../components/AdvancedNumberInput'
import AdvancedPriceNumberInput from '../../components/AdvancedPriceNumberInput'
import AdvancedSelectInput from '../../components/AdvancedSelectInput'
import BasicFormToolbar from '../../components/BasicFormToolbar'
import { EnumSelectInput } from '../../components/EnumSelectInput'
import GroupMembershipInput from '../../components/GroupMembershipInput'
import { SYSTEM_PERMISSION_READ } from '../../config/permissions'
import { useCommonStyles } from '../../config/theme'
import {
  VEHICLE_COMBUSTION_TYPES,
  VEHICLE_ENERGY_TYPES,
  VEHICLE_GATEWAYS,
  VEHICLE_SPECIAL_FIELD_CODE_CHARGING_INSTRUCTIONS,
  VEHICLE_SPECIAL_FIELD_CODE_FUEL_CARD_PIN,
  VEHICLE_SPECIAL_FIELD_CODE_FUELING_INFO,
  VEHICLE_SPECIAL_FIELD_CODE_PARKING_SPOT,
  VEHICLE_SPECIAL_FIELDS,
  VEHICLE_TRANSMISSION_TYPES,
  VEHICLE_TYPE_CAR_BLUEPRINT_SETS,
  VEHICLE_TYPES,
} from '../../config/vehicles'
import { useIsFieldPermissionDenied, useResourcePermissions } from '../../domain/permissions'
import { isVehicleCar, isVehicleChargeableCar, isVehicleElectricCar, isVehicleFuelCar } from '../../domain/vehicles'
import { getRoundedNowJS, parseDateAsDateTime, validateEndDateAfterStartDate } from '../../utils/dates'
import { useSmallScreen } from '../../utils/theme'
import { validateVIN } from '../../utils/validators'
import bookingConfig from '../bookings/config'
import { FormDivider } from '../common'
import { AdvancedSimpleForm, FormSubTitle } from '../common/forms'
import useGroupMembershipRecord from '../common/useGroupMembershipRecord'
import HubReferenceInput from '../hubs/input'
import organisationsConfig from '../organisations/config'
import OrganisationReferenceInput from '../organisations/input'
import vehicleUnavailabilitiesConfig from '../vehicleUnavailabilities/config'

const vehicleBlueprintSetChoices = Object.entries(VEHICLE_TYPE_CAR_BLUEPRINT_SETS).map(([k, v]) => ({ id: k, name: v }))
const vehicleGatewayCodeChoices = Object.entries(VEHICLE_GATEWAYS).map(([k, v]) => ({ id: k, name: v }))

const VehicleSpecialFieldInput = ({ code, ...props }) => (
  <TextInput {...props} source={'details_object.' + code} label={VEHICLE_SPECIAL_FIELDS[code]} />
)

const PricePerKmNumberInput = ({ organisationId, source, type, ...props }) => {
  const { setValue } = useFormContext()

  const [hasReadForOrganisations] = useResourcePermissions(organisationsConfig.name, SYSTEM_PERMISSION_READ)
  const { data: organisation } = useGetOne(
    organisationsConfig.name,
    { id: organisationId },
    { enabled: hasReadForOrganisations && Boolean(organisationId) },
  )
  const organisationPricePerKm = get(organisation, 'price_per_km', null)
  const [pricePerKm, setPricePerKm] = useState(organisationPricePerKm)

  useEffect(() => {
    setPricePerKm(organisationPricePerKm)
  }, [organisationId, organisationPricePerKm])

  useEffect(() => {
    if (type === CREATE) {
      setValue(source, pricePerKm)
    }
  }, [setValue, type, pricePerKm, source])

  return <AdvancedPriceNumberInput source={source} validate={required()} {...props} />
}

const getFieldCollectionFromObject = (obj) =>
  obj
    ? Object.entries(obj)
        .map(([k, v]) => ({ code: k, value: v }))
        .filter((i) => i.value !== null && i.value !== undefined) // remove items with no value
    : undefined

const getFieldObjectFromCollection = (coll) =>
  coll && Array.isArray(coll) ? coll.reduce((acc, field) => ({ ...acc, [field.code]: field.value }), {}) : coll

const getVehicleFormRecordDefaults = (record, translate) => {
  if (!record) return {}

  return {
    ...record,
    picture_data: record.picture ? { src: record.picture } : null,
    information_document_data: record.information_document
      ? {
          src: record.information_document,
          title: translate('resources.vehicles.fields.information_document_data'),
        }
      : null,
    details_object: getFieldObjectFromCollection(record.details),
    gateway: pick(record.gateway, ['code', 'vehicle_id']),
  }
}

export const transformValues = ({
  charging_buffer,
  details_object,
  information_document_data,
  price_per_km,
  ...restValues
}) =>
  restValues
    ? {
        ...restValues,
        charging_buffer: isVehicleElectricCar(restValues) ? charging_buffer : 0,
        details: getFieldCollectionFromObject(details_object),
        information_document_data:
          information_document_data && isVehicleCar(restValues) ? information_document_data : null,
        price_per_km: isVehicleCar(restValues) ? price_per_km : 0,
      }
    : restValues

const VehicleIdentificationSection = ({ type, commonClasses, translate }) => {
  const [vehicleType] = useWatch({ name: ['type'] })
  const isCar = isVehicleCar({ type: vehicleType })

  return (
    <>
      <FormSubTitle text="identification" isFirstChild />

      <ImageInput
        source="picture_data"
        sx={{ ...commonClasses.commonInput, ...commonClasses.imageInput }}
        labelSingle={translate('mymove.dropOrSelectYourWebpFileHere')}
        helperText={false}
        accept={{ 'image/webp': ['.webp'] }}
        validate={required()}
      >
        <ImageField source="src" title="title" />
      </ImageInput>

      <FormDivider />

      <EnumSelectInput options={VEHICLE_TYPES} source="type" validate={required()} readOnly={type === UPDATE} />

      {isCar && (
        <SelectInput
          choices={vehicleBlueprintSetChoices}
          source="blueprint_set"
          validate={required()}
          readOnly={type === UPDATE}
        />
      )}

      <FormDivider />

      <TextInput source="brand" validate={required()} />
      <TextInput source="model" validate={required()} />
      <TextInput source="designation" validate={required()} />
    </>
  )
}

const VehicleTechnicalCharacteristicsSection = () => {
  const [vehicleType, energyType] = useWatch({ name: ['type', 'energy_type'] })
  const formData = { type: vehicleType, energy_type: energyType }

  return (
    <>
      <FormSubTitle text="technicalCharacteristics" />
      <AdvancedColorAutocompleteInput />

      {isVehicleCar(formData) && (
        <>
          <AdvancedNumberInput source="year" validate={required()} min={1950} />
          <TextInput source="identification_number" validate={validateVIN()} />
          <EnumSelectInput options={VEHICLE_TRANSMISSION_TYPES} source="transmission_type" validate={required()} />
          <AdvancedSelectInput source="keyless" />
          <AdvancedNumberInput source="number_of_seats" min={1} validate={required()} />
          <AdvancedNumberInput source="number_of_doors" min={1} validate={required()} />
          <AdvancedNumberInput source="volume" min={1} unit="m³" />
          <AdvancedPriceNumberInput source="value" step={10} />

          <FormDivider />

          <EnumSelectInput options={VEHICLE_ENERGY_TYPES} source="energy_type" validate={required()} />

          {isVehicleElectricCar(formData) && (
            <AdvancedNumberInput source="maximum_autonomy" min={0} unit="km" validate={required()} />
          )}

          {isVehicleChargeableCar(formData) && (
            <VehicleSpecialFieldInput code={VEHICLE_SPECIAL_FIELD_CODE_CHARGING_INSTRUCTIONS} />
          )}

          {isVehicleFuelCar(formData) && (
            <>
              <EnumSelectInput source="combustion_type" options={VEHICLE_COMBUSTION_TYPES} validate={required()} />
              <AdvancedNumberInput source="tank_size" min={0} unit="L" validate={required()} />
              <AdvancedNumberInput
                source="average_consumption"
                step={0.1}
                min={0}
                unit="L/100km"
                validate={required()}
              />
              <VehicleSpecialFieldInput code={VEHICLE_SPECIAL_FIELD_CODE_FUELING_INFO} />
            </>
          )}
        </>
      )}
    </>
  )
}

const VehicleAssignmentSection = ({ validateOrganisationChange, type }) => {
  const [startedOn] = useWatch({ name: ['started_on'] })
  const isPermissionForOrganisationDenied = useIsFieldPermissionDenied({ action: type, source: 'organisation_id' })

  return (
    <>
      <FormSubTitle text="assignment" />

      <OrganisationReferenceInput
        source="organisation_id"
        validate={[required(), validateOrganisationChange]}
        readOnly={isPermissionForOrganisationDenied}
      />
      <HubReferenceInput />

      <FormDivider />

      <AdvancedDateTimeInput source="started_on" validate={required()} />
      <AdvancedDateTimeInput
        source="ended_on"
        minDate={startedOn ? parseDateAsDateTime(startedOn) : undefined}
        validate={(v) => validateEndDateAfterStartDate(startedOn, v)}
      />
    </>
  )
}

const VehiclePricingSection = ({ type }) => {
  const [vehicleType, organisationId] = useWatch({ name: ['type', 'organisation_id'] })
  const formData = { type: vehicleType }

  return (
    <>
      <FormSubTitle text="pricing" />

      <AdvancedNumberInput source="price_factor" step={0.01} min={0} validate={required()} />
      <AdvancedPriceNumberInput source="security_deposit_amount" validate={required()} step={10} defaultValue={0} />

      {isVehicleCar(formData) && (
        <PricePerKmNumberInput organisationId={organisationId} source="price_per_km" type={type} />
      )}
    </>
  )
}

const VehicleOperationalDetailsSection = ({ commonClasses, isSmallScreen, translate }) => {
  const [vehicleType, energyType] = useWatch({ name: ['type', 'energy_type'] })
  const formData = { type: vehicleType, energy_type: energyType }

  return (
    <>
      <FormSubTitle text="operationalDetails" />

      <VehicleSpecialFieldInput code={VEHICLE_SPECIAL_FIELD_CODE_PARKING_SPOT} />

      {isVehicleElectricCar(formData) && (
        <AdvancedNumberInput
          source="charging_buffer"
          format={(v) => (v == null ? null : v / 60)}
          parse={(v) => (v == null ? null : v * 60)}
          min={0}
          max={24}
          defaultValue={0}
          unit="mymove.units.time.hours"
        />
      )}

      {isVehicleFuelCar(formData) && <VehicleSpecialFieldInput code={VEHICLE_SPECIAL_FIELD_CODE_FUEL_CARD_PIN} />}

      {Boolean(vehicleType) && !isVehicleCar(formData) && (
        <VehicleSpecialFieldInput code={VEHICLE_SPECIAL_FIELD_CODE_CHARGING_INSTRUCTIONS} />
      )}

      {isVehicleCar(formData) && (
        <>
          <FormDivider />

          <ImageInput
            accept={{ 'application/pdf': ['.pdf'] }}
            source="information_document_data"
            labelSingle={translate('mymove.dropOrSelectYourPDFDocumentHere')}
            sx={{
              ...(isSmallScreen ? commonClasses.commonInput : commonClasses.doubleInput),
              ...commonClasses.imageInput,
            }}
            helperText={false}
          >
            <ImageField source="src" title="title" />
          </ImageInput>
        </>
      )}
    </>
  )
}

const VehicleProviderDetailsSection = ({ type }) => {
  const isPermissionForGatewayCodeDenied = useIsFieldPermissionDenied({ action: type, source: 'gateway.code' })
  const isPermissionForGatewayVehicleIdDenied = useIsFieldPermissionDenied({
    action: type,
    source: 'gateway.vehicle_id',
  })

  return (
    <>
      <FormSubTitle text="providerDetails" />
      <SelectInput
        choices={vehicleGatewayCodeChoices}
        source="gateway.code"
        validate={required()}
        readOnly={isPermissionForGatewayCodeDenied}
      />
      <TextInput source="gateway.vehicle_id" readOnly={isPermissionForGatewayVehicleIdDenied} />
      <AdvancedSelectInput source="nfc_access_supported" />
    </>
  )
}

const VehicleFormLayout = ({ type = UPDATE, defaultValues = {}, record: recordProp }) => {
  const recordContext = useRecordContext()
  const { record, isPending: membershipsPending } = useGroupMembershipRecord(
    'vehicles',
    'vehicle_group_ids',
    recordProp ?? recordContext,
    { enabled: type === UPDATE },
  )
  const translate = useTranslate()

  const isSmallScreen = useSmallScreen()
  const commonClasses = useCommonStyles()

  const now = useRef(getRoundedNowJS())

  const commonParams = {
    params: { pagination: { page: 1, perPage: 1 }, filter: { vehicle_id: record?.id, timeline_start: now.current } },
    options: { enabled: Boolean(record?.id) },
  }

  const { total: numOfBookings } = useGetList(
    bookingConfig.name,
    {
      ...commonParams.params,
      filter: { ...commonParams.params.filter, ...bookingConfig.options.defaultFilterValues },
    },
    commonParams.options,
  )

  const { total: numOfUnavailabilities } = useGetList(
    vehicleUnavailabilitiesConfig.name,
    commonParams.params,
    commonParams.options,
  )

  const validateOrganisationChange = (v) => {
    if (type === UPDATE && record.organisation_id !== v && (numOfBookings > 0 || numOfUnavailabilities > 0)) {
      return { message: 'mymove.validation.organisationChangeWithUpcomingBookingsOrUnavailabilities' }
    }
  }

  const normalizedRecordDefaults = useMemo(
    () => (type === UPDATE ? getVehicleFormRecordDefaults(record, translate) : {}),
    [record, translate, type],
  )

  if (membershipsPending) return null

  return (
    <AdvancedSimpleForm
      defaultValues={{ started_on: now.current, price_factor: 1, ...normalizedRecordDefaults, ...defaultValues }}
      draftable={type === CREATE}
      toolbar={<BasicFormToolbar draftable={type === CREATE} formType={type} />}
      type={type}
    >
      <VehicleIdentificationSection type={type} commonClasses={commonClasses} translate={translate} />
      <VehicleTechnicalCharacteristicsSection />
      <VehicleAssignmentSection validateOrganisationChange={validateOrganisationChange} type={type} />
      {type === UPDATE && Array.isArray(record?.vehicle_group_ids) && (
        <>
          <FormDivider />
          <GroupMembershipInput source="vehicle_group_ids" reference="vehicle-groups" />
        </>
      )}
      <VehiclePricingSection type={type} />
      <VehicleOperationalDetailsSection
        commonClasses={commonClasses}
        isSmallScreen={isSmallScreen}
        translate={translate}
      />
      <VehicleProviderDetailsSection type={type} />
    </AdvancedSimpleForm>
  )
}

export default VehicleFormLayout
